import { msg } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { useMutation } from '@tanstack/react-query';
import { BackendNoAuthClient } from '@tf/api';
import {
	AppShell,
	Box,
	createStyles,
	Divider,
	MobileNotice,
	Stack,
	TFCard,
	useLocalStorage,
	useUpdateBodyBackground,
	useViewportSize,
} from '@tf/ui';
import { useEffect } from 'react';
import { SignUpForm } from '@/components/auth';
import { CompanyLogo } from '@/components/shared';
import { Footer } from '@/components/shared/AppLayout/Footer';
import { useConfig, useDefinitions } from '@/core/providers';
import { useCurrentLocale } from '@/core/providers/I18nProvider';
import { useOriginTenantSlug } from '@/core/providers/OriginTenantSlugProvider';

import { usePageTitle } from '../../usePageTitle';

const useStyles = createStyles(() => ({
	root: {
		display: 'flex',
		height: '100vh',
		background: '#F9FAFB',
	},
	welcomeText: {
		padding: '2rem 4rem',
		flex: '0 0 55%',
		fontSize: '1.125rem',
		color: '#E4ECFD',
		background: '#051A44',
		backgroundImage: 'linear-gradient(90deg, #051A44 0%, #072664 100%)',
	},
	form: {
		padding: '10% 2rem 2rem',
		flex: 1,
		display: 'flex',
		justifyContent: 'center',
	},
}));

export const SignUpRoute = () => {
	const { _ } = useLingui();
	const currentLocale = useCurrentLocale();
	const { classes } = useStyles();
	const { revision } = useDefinitions();
	const config = useConfig();
	const originTenantSlug = useOriginTenantSlug();

	useUpdateBodyBackground('#F9FAFB');

	useEffect(() => {
		BackendNoAuthClient.init(config.urls.backend);
	}, [config]);

	const { mutateAsync: login, isPending } = useMutation({
		mutationFn: async (payload: { email: string }): Promise<void> => {
			await BackendNoAuthClient.getInstance().signup(
				{
					...payload,
					originTenantSlug,
				},
				{
					headers: {
						'X-Locale': currentLocale,
						'Configuration-Revision': revision,
					},
				},
			);
		},
	});

	usePageTitle(_(msg({ id: 'loginPage.title', message: 'Log in' })));

	const viewport = useViewportSize();
	const [agreeWithNotice, setAgreeWithNotice] = useLocalStorage({
		defaultValue: false,
		key: 'client_area-mobile-notice',
	});
	if (viewport.width < 743 && !agreeWithNotice) {
		return <MobileNotice onContinue={() => setAgreeWithNotice(true)} />;
	}

	return (
		<AppShell layout="alt">
			<Stack className={classes.root}>
				<Box className={classes.form}>
					<TFCard w="432px">
						<Box p="8px 16px 16px 16px">
							<Stack w="100%" gap="0" align="center">
								<CompanyLogo />
								<Divider w="100%" size="1px" m="12px 24px" color="#E5E7EB" />
								<SignUpForm isLoading={isPending} onLogin={login} />
							</Stack>
						</Box>
					</TFCard>
				</Box>
				<Footer withBorder />
			</Stack>
		</AppShell>
	);
};
